/**
 * Pieces jointes de coffre : un fichier (image, PDF, code de secours...) lie a
 * une entree, chiffre comme le contenu d'une entree, sous la `vaultKey`, via
 * `@klavox/crypto-core`. Seuls `id`, `itemId`, `revision` et les horodatages
 * restent en clair — le NOM du fichier et son contenu (`dataBase64`) sont
 * chiffres : un nom de fichier peut etre aussi sensible qu'un nom de dossier
 * (`folder.ts`). Les metadonnees claires sont AUTHENTIFIEES (liees a l'AEAD,
 * `aad.ts`).
 */
import {
  base64ToBytes,
  decryptJson,
  encryptJson,
  type EncryptedEntry,
  type SecretKey,
} from '@klavox/crypto-core';
import { assertCount, assertId, assertNullableCount, openWithLegacyFallback, recordAad } from './aad.js';
import { resolveNow } from './clock.js';
import { InvalidVaultModelInputError } from './errors.js';
import { generateId } from './ids.js';

/** Taille maximale du CONTENU d'une piece jointe (octets en clair) : 10 Mio. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Longueur maximale d'un nom de fichier (caracteres). */
export const MAX_ATTACHMENT_FILE_NAME_LENGTH = 255;

/**
 * Longueur maximale du blob chiffre (`secrets`) d'une piece jointe : un
 * contenu de `MAX_ATTACHMENT_BYTES` passe deux fois par base64 (contenu dans
 * le JSON, puis blob scelle) soit ~18,7 M caracteres, plus nom et en-tete.
 * Borne verifiable SANS dechiffrer (serveur).
 */
export const MAX_ATTACHMENT_CIPHERTEXT_LENGTH = 19_000_000;

/**
 * Quota de pieces jointes d'un coffre, mesure sur les blobs chiffres des pieces
 * ACTIVES (ce que stocke le serveur) : ~100 Mio de fichiers en clair.
 */
export const MAX_VAULT_ATTACHMENTS_STORAGE = 190_000_000;

/** Partie SECRETE d'une piece jointe : nom, type MIME et contenu (base64). */
export interface VaultAttachmentSecrets {
  readonly fileName: string;
  readonly mimeType?: string;
  /** Contenu binaire du fichier, encode en base64 (le JSON ne porte pas de binaire). */
  readonly dataBase64: string;
}

/** Metadonnees non secretes, communes a la piece jointe en clair et chiffree. */
export interface VaultAttachmentMetadata {
  readonly id: string;
  /** `id` de l'entree (`VaultItem`) a laquelle cette piece jointe est liee. */
  readonly itemId: string;
  readonly revision: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Horodatage de suppression (tombstone) ; `null` tant que la piece est active. */
  readonly deletedAt: number | null;
}

/** Piece jointe dechiffree : `secrets` en clair, a garder en memoire uniquement. */
export interface VaultAttachment extends VaultAttachmentMetadata {
  readonly secrets: VaultAttachmentSecrets;
}

/** Piece jointe telle que stockee/synchronisee : `secrets` est un blob opaque. */
export interface EncryptedVaultAttachment extends VaultAttachmentMetadata {
  readonly secrets: EncryptedEntry;
}

export interface CreateVaultAttachmentInput {
  readonly itemId: string;
  readonly fileName: string;
  readonly mimeType?: string;
  readonly dataBase64: string;
  readonly now?: number;
}

function assertVaultAttachmentMetadata(attachment: unknown): asserts attachment is VaultAttachmentMetadata {
  if (attachment === null || typeof attachment !== 'object') {
    throw new InvalidVaultModelInputError('attachment: objet attendu');
  }
  const candidate = attachment as Partial<VaultAttachmentMetadata>;
  if (typeof candidate.id !== 'string' || candidate.id.length === 0) {
    throw new InvalidVaultModelInputError('attachment.id: chaine non vide attendue');
  }
}

function assertAttachmentSecrets(secrets: unknown): asserts secrets is VaultAttachmentSecrets {
  if (secrets === null || typeof secrets !== 'object') {
    throw new InvalidVaultModelInputError('secrets: objet attendu');
  }
  const candidate = secrets as Partial<VaultAttachmentSecrets>;
  if (typeof candidate.fileName !== 'string' || candidate.fileName.length === 0) {
    throw new InvalidVaultModelInputError('fileName: chaine non vide attendue');
  }
  if (candidate.fileName.length > MAX_ATTACHMENT_FILE_NAME_LENGTH) {
    throw new InvalidVaultModelInputError(`fileName: ${MAX_ATTACHMENT_FILE_NAME_LENGTH} caracteres maximum`);
  }
  if (typeof candidate.dataBase64 !== 'string' || candidate.dataBase64.length === 0) {
    throw new InvalidVaultModelInputError('dataBase64: chaine non vide attendue');
  }
  // Rejette tot un contenu corrompu (base64 mal forme) plutot que de le
  // decouvrir bien plus tard, au dechiffrement, sous un message ambigu.
  let size: number;
  try {
    size = base64ToBytes(candidate.dataBase64).length;
  } catch {
    throw new InvalidVaultModelInputError('dataBase64: base64 invalide');
  }
  if (size > MAX_ATTACHMENT_BYTES) {
    throw new InvalidVaultModelInputError(`dataBase64: ${MAX_ATTACHMENT_BYTES} octets maximum`);
  }
}

/** Cree une nouvelle piece jointe (revision 1, non supprimee). */
export function createVaultAttachment(input: CreateVaultAttachmentInput): VaultAttachment {
  if (input === null || typeof input !== 'object') {
    throw new InvalidVaultModelInputError('input: objet attendu');
  }
  if (typeof input.itemId !== 'string' || input.itemId.length === 0) {
    throw new InvalidVaultModelInputError('itemId: chaine non vide attendue');
  }
  const secrets: VaultAttachmentSecrets = {
    fileName: input.fileName,
    mimeType: input.mimeType,
    dataBase64: input.dataBase64,
  };
  assertAttachmentSecrets(secrets);
  const now = resolveNow(input.now);
  return {
    id: generateId(),
    itemId: input.itemId,
    revision: 1,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    secrets,
  };
}

function strictVaultAttachmentMetadata(attachment: unknown): VaultAttachmentMetadata {
  if (attachment === null || typeof attachment !== 'object') {
    throw new InvalidVaultModelInputError('attachment: objet attendu');
  }
  const c = attachment as Record<keyof VaultAttachmentMetadata, unknown>;
  assertId(c.id, 'attachment.id');
  assertId(c.itemId, 'attachment.itemId');
  assertCount(c.revision, 'attachment.revision');
  assertCount(c.createdAt, 'attachment.createdAt');
  assertCount(c.updatedAt, 'attachment.updatedAt');
  assertNullableCount(c.deletedAt, 'attachment.deletedAt');
  return {
    id: c.id,
    itemId: c.itemId,
    revision: c.revision,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    deletedAt: c.deletedAt,
  };
}

function vaultAttachmentAad(m: VaultAttachmentMetadata): string {
  return recordAad('attachment', [m.id, m.itemId, m.revision, m.createdAt, m.updatedAt, m.deletedAt]);
}

export interface EncryptVaultAttachmentInput {
  readonly attachment: VaultAttachment;
  readonly vaultKey: SecretKey;
}

/**
 * Chiffre les `secrets` (nom + contenu) de la piece jointe sous la
 * `vaultKey`, metadonnees liees en AAD (format v2) : un serveur malveillant ne
 * peut ni substituer le blob d'une autre piece jointe, ni la rattacher a une
 * autre entree, ni forger sa revision ou sa suppression.
 */
export function encryptVaultAttachment(input: EncryptVaultAttachmentInput): EncryptedVaultAttachment {
  const metadata = strictVaultAttachmentMetadata(input?.attachment);
  const ciphertext = encryptJson({
    entry: input.attachment.secrets,
    vaultKey: input.vaultKey,
    entryId: vaultAttachmentAad(metadata),
  });
  return { ...metadata, secrets: ciphertext };
}

export interface DecryptVaultAttachmentInput {
  readonly attachment: EncryptedVaultAttachment;
  readonly vaultKey: SecretKey;
  /** Voir `DecryptVaultItemInput.acceptLegacyFormat` : migration locale uniquement. */
  readonly acceptLegacyFormat?: boolean;
}

/**
 * Dechiffre une piece jointe.
 *
 * @throws {import('@klavox/crypto-core').DecryptionFailedError} mauvaise
 *   `vaultKey`, blob altere, blob substitue depuis une autre piece jointe, ou
 *   metadonnee modifiee depuis le chiffrement
 */
export function decryptVaultAttachment(input: DecryptVaultAttachmentInput): VaultAttachment {
  const metadata = strictVaultAttachmentMetadata(input?.attachment);
  const open = (entryId: string) => () =>
    decryptJson<VaultAttachmentSecrets>({ ciphertext: input.attachment.secrets, vaultKey: input.vaultKey, entryId });
  const plaintext = openWithLegacyFallback(input.acceptLegacyFormat, open(vaultAttachmentAad(metadata)), open(metadata.id));
  assertAttachmentSecrets(plaintext);
  return { ...metadata, secrets: plaintext };
}

/**
 * Suppression logique (tombstone) : la piece jointe garde son ciphertext mais
 * porte `deletedAt`, pour que la synchro propage la suppression aux autres
 * appareils avant purge definitive (symetrique de `markVaultItemDeleted`).
 */
export function markVaultAttachmentDeleted<T extends VaultAttachmentMetadata>(attachment: T, now?: number): T {
  assertVaultAttachmentMetadata(attachment);
  const at = resolveNow(now);
  if (attachment.deletedAt !== null) return attachment;
  return { ...attachment, deletedAt: at, revision: attachment.revision + 1, updatedAt: at } as T;
}

/** Annule une suppression logique tant que la piece jointe n'a pas ete purgee. */
export function restoreVaultAttachment<T extends VaultAttachmentMetadata>(attachment: T, now?: number): T {
  assertVaultAttachmentMetadata(attachment);
  if (attachment.deletedAt === null) return attachment;
  return { ...attachment, deletedAt: null, revision: attachment.revision + 1, updatedAt: resolveNow(now) } as T;
}
